import React, { createContext, useContext, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Replaces the old static src/params.js. Which conference is "active" and
// everything about it (deadlines, prices, discount codes, enabled steps,
// banner/markdown content) now lives in Firestore under
// settings/active -> { conferenceId } and conferences/{conferenceId}, so a
// new conference can be stood up by writing data, not by editing/redeploying
// this app.
const ConferenceConfigContext = createContext();

export function useConferenceConfig() {
  const ctx = useContext(ConferenceConfigContext);
  if (!ctx) {
    throw new Error('useConferenceConfig must be used within a ConferenceConfigProvider');
  }
  return ctx;
}

export function ConferenceConfigProvider({ children }) {
  const [state, setState] = useState({ status: 'loading', conferenceId: null, config: null, error: null });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const activeSnap = await getDoc(doc(db, 'settings', 'active'));
        const conferenceId = activeSnap.data()?.conferenceId;
        if (!conferenceId) {
          throw new Error('settings/active is missing or has no conferenceId set.');
        }

        const confSnap = await getDoc(doc(db, 'conferences', conferenceId));
        if (!confSnap.exists()) {
          throw new Error(`conferences/${conferenceId} does not exist.`);
        }

        if (!cancelled) {
          setState({ status: 'ready', conferenceId, config: confSnap.data(), error: null });
        }
      } catch (error) {
        console.error('Failed to load conference config:', error);
        if (!cancelled) {
          setState({ status: 'error', conferenceId: null, config: null, error });
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <ConferenceConfigContext.Provider value={state}>
      {children}
    </ConferenceConfigContext.Provider>
  );
}
